import { useClientPortalAPI } from "@certn/api";
import { components } from "@certn/api/generated-schemas/client-portal";
import {
  Container,
  Layout,
  Loader,
  SectionMessage,
} from "@certn/cascadia-react";
import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import { SimpleHeader } from "./SimpleHeader";

type Permission = components["schemas"]["LoggedInUserPermissionsList"];

const MAX_EMPTY_RESPONSE_RECOVERY_ATTEMPTS = 1;

interface PermissionsContextProps {
  hasPermission: (requiredPermission: Permission | Permission[]) => boolean;
}

export const PermissionsProviderContext =
  createContext<PermissionsContextProps>({
    hasPermission: () => false,
  });

export function usePermissions() {
  return useContext(PermissionsProviderContext);
}

export function PermissionsProvider(props: PropsWithChildren) {
  const { t } = useTranslation();

  const { children } = props;

  const {
    data: permissions,
    isLoading,
    isValidating,
    error,
    mutate,
  } = useClientPortalAPI(`/api/internal/client-portal/me/permissions/`);

  const recoveryAttemptedRef = useRef(false);
  const [recoveryAttempts, setRecoveryAttempts] = useState(0);

  // SWR can settle with neither data nor error when a response is discarded
  // (e.g. a bootstrap cache purge raced the request). SWR only retries errors,
  // so without a manual revalidate the user is stuck until a full reload.
  const isSettledWithoutData =
    !permissions && !error && !isLoading && !isValidating;
  const canRecover = recoveryAttempts < MAX_EMPTY_RESPONSE_RECOVERY_ATTEMPTS;

  useEffect(() => {
    if (!isSettledWithoutData || recoveryAttemptedRef.current) {
      return;
    }
    recoveryAttemptedRef.current = true;
    setRecoveryAttempts((attempts) => attempts + 1);
    void mutate();
  }, [isSettledWithoutData, mutate]);

  const hasPermission = useCallback(
    (requiredPermission: Permission | Permission[]) => {
      if (!permissions) {
        return false;
      }

      const matches =
        (requiredPermission: Permission) => (permission: Permission) =>
          requiredPermission.action === permission.action &&
          requiredPermission.target === permission.target &&
          requiredPermission.scope === permission.scope;

      if (Array.isArray(requiredPermission)) {
        return requiredPermission.some((requiredPermission) =>
          permissions.some(matches(requiredPermission))
        );
      }
      return permissions.some(matches(requiredPermission));
    },
    [permissions]
  );

  if (
    !permissions &&
    !error &&
    (isLoading || isValidating || (isSettledWithoutData && canRecover))
  ) {
    return <Loader overlay />;
  }

  if (!permissions) {
    return (
      <Layout header={<SimpleHeader />}>
        <Container>
          <SectionMessage
            sectionTitle={t("Unable load permissions for your user.")}
          >
            {error
              ? String(error)
              : t(
                  "Are you logged in? Try logging out and logging back in again."
                )}
          </SectionMessage>
        </Container>
      </Layout>
    );
  }

  return (
    <PermissionsProviderContext.Provider value={{ hasPermission }}>
      {children}
    </PermissionsProviderContext.Provider>
  );
}
